import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { track } from '../lib/analytics';
import { BACKEND_URL } from '../lib/apiBase';

/**
 * CurrencyContext
 * ---------------
 * Lightweight client-side currency conversion.  Prices in the API are stored in
 * EUR (the base currency).  This context exposes:
 *   - `currency`            current currency code ("EUR", "USD", …)
 *   - `setCurrency(code)`   change currency & persist to localStorage
 *   - `formatPrice(amount)` convert a EUR price to the active currency and
 *                           return a properly localised string
 *   - `CURRENCIES`          metadata for every supported currency
 *
 * Rates are fetched live from `/api/currency/rates` (backed by frankfurter.dev,
 * cached server-side for 12h) on first mount.  The hardcoded values below act
 * as an offline-safe fallback so the UI never breaks if the network call
 * fails.  Live rates are also cached in localStorage for 12h to keep the
 * first paint fast across visits.
 */

const RATES_STORAGE_KEY = 'sv_rates_v1';
const RATES_TTL_MS = 12 * 60 * 60 * 1000; // 12h, mirrors backend TTL

export const CURRENCIES = {
  EUR: { code: 'EUR', symbol: '€',  label: 'Euro',              rateFromEUR: 1,     decimals: 2 },
  USD: { code: 'USD', symbol: '$',  label: 'US Dollar',         rateFromEUR: 1.08,  decimals: 2 },
  GBP: { code: 'GBP', symbol: '£',  label: 'British Pound',     rateFromEUR: 0.86,  decimals: 2 },
  CHF: { code: 'CHF', symbol: 'Fr', label: 'Swiss Franc',       rateFromEUR: 0.95,  decimals: 2 },
  CAD: { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar',   rateFromEUR: 1.49,  decimals: 2 },
  AUD: { code: 'AUD', symbol: 'A$', label: 'Australian Dollar', rateFromEUR: 1.64,  decimals: 2 },
  JPY: { code: 'JPY', symbol: '¥',  label: 'Japanese Yen',      rateFromEUR: 163,   decimals: 0 },
  CNY: { code: 'CNY', symbol: '¥',  label: 'Chinese Yuan',      rateFromEUR: 7.85,  decimals: 2 },
};

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCIES);
const DEFAULT_CURRENCY = 'EUR';
const STORAGE_KEY = 'sv_currency';

/**
 * Currency detection rules (first visit only — explicit choice always wins):
 *
 *   A. English variants
 *        en-GB                  → GBP
 *        en-US                  → USD
 *        en-CA                  → CAD
 *        en-AU / en-NZ          → AUD
 *        en-IE                  → EUR
 *        any other en-XX / bare → USD
 *
 *   B. Languages with a site translation
 *        ja                     → JPY
 *        zh-CN / bare zh / Hans → CNY
 *        zh-TW / zh-HK / Hant   → USD
 *        de, es, ca, fr, it, pt,
 *        pl, ru, uk             → EUR
 *
 *   C. Languages WITHOUT a site translation
 *        European locale*       → EUR  (Scandinavian, Greek, Czech, Hungarian,
 *                                       Romanian, Baltic, Balkan, etc.)
 *        Non-European locale    → USD  (Arabic, Korean, Hindi, Thai, …)
 *
 *   *European = primary language is a known European tag, OR the BCP-47
 *    region subtag is a European country (incl. UK, EFTA, Balkans).
 */

// Translation languages that are NOT English/Chinese/Japanese all map to EUR.
const TRANSLATION_LANGS_EUR = new Set([
  'de', 'es', 'ca', 'fr', 'it', 'pt', 'pl', 'ru', 'uk',
]);

// English region → currency. Anything not listed falls back to USD.
const EN_REGION_CURRENCY = {
  gb: 'GBP', uk: 'GBP',
  us: 'USD',
  ca: 'CAD',
  au: 'AUD',
  nz: 'AUD',
  ie: 'EUR',
};

// ISO-3166 alpha-2 codes considered "European" for currency purposes.
const EUROPEAN_COUNTRIES = new Set([
  // EU
  'at','be','bg','hr','cy','cz','dk','ee','fi','fr','de','gr','hu','ie',
  'it','lv','lt','lu','mt','nl','pl','pt','ro','sk','si','es','se',
  // EFTA / micro-states
  'is','no','li','ch','ad','mc','sm','va','gi',
  // UK + crown deps
  'gb','gg','je','im','fo',
  // Balkans / E-Europe / Caucasus (Europe-leaning)
  'al','ba','mk','me','rs','xk','md','ua','by','ru','tr',
]);

// Primary language subtags considered "European" when no region is given.
// Includes Scandinavian, Baltic, Balkan, Greek, Celtic, etc.
const EUROPEAN_PRIMARY_LANGS = new Set([
  // Scandinavian
  'sv','no','nb','nn','da','fi','is','fo',
  // Greek + Celtic + minority
  'el','ga','cy','gd','br','eu','gl','lb','rm',
  // Central / Eastern
  'cs','sk','hu','ro','bg','hr','sl','sr','bs','mk','sq','mt',
  // Baltic
  'et','lv','lt',
  // Other EU primary languages (we have translations for these but list
  // them anyway so European bare tags always resolve correctly)
  'de','es','ca','fr','it','pt','pl','nl',
  // Slavic East
  'ru','uk','be',
  // Turkish (Europe-leaning for site purposes)
  'tr',
]);

function pickCurrencyForLocale(locale) {
  if (!locale) return DEFAULT_CURRENCY;
  const parts = locale.toLowerCase().split(/[-_]/);
  const lang = parts[0];
  // Region is the first 2-letter subtag after the language (skip script
  // tags like "Hans"/"Hant" which are 4 letters).
  const region = parts.slice(1).find((p) => p.length === 2) || '';
  const script = parts.slice(1).find((p) => p.length === 4) || '';

  // A. English variants
  if (lang === 'en') {
    return EN_REGION_CURRENCY[region] || 'USD';
  }

  // B. Translation languages with special currencies
  if (lang === 'ja') return 'JPY';
  if (lang === 'zh') {
    // zh-Hans / zh-CN / bare zh → mainland → CNY
    // zh-Hant / zh-TW / zh-HK / zh-SG → USD
    if (script === 'hant' || ['tw', 'hk', 'mo', 'sg'].includes(region)) return 'USD';
    return 'CNY';
  }

  // B (cont). Other translation languages → EUR
  if (TRANSLATION_LANGS_EUR.has(lang)) return 'EUR';

  // C. No translation — European stays in EUR, rest USD.
  const isEuropean =
    (region && EUROPEAN_COUNTRIES.has(region)) ||
    EUROPEAN_PRIMARY_LANGS.has(lang);
  return isEuropean ? 'EUR' : 'USD';
}

function detectInitialCurrency() {
  if (typeof window === 'undefined') return DEFAULT_CURRENCY;

  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_CURRENCIES.includes(stored)) return stored;
  } catch (_) {
    /* localStorage may be unavailable */
  }

  // Only the user's PRIMARY language is consulted. Trailing OS-level
  // fallbacks (e.g. an "en-US" appended after "de-DE") must not override
  // a deliberate primary preference.
  const primaryRaw = (navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language) || '';
  const picked = pickCurrencyForLocale(primaryRaw);
  return SUPPORTED_CURRENCIES.includes(picked) ? picked : DEFAULT_CURRENCY;
}

// Exported for unit tests.
export { pickCurrencyForLocale };

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(detectInitialCurrency);

  // Live rates loaded from /api/currency/rates. Falls back to the hardcoded
  // values in CURRENCIES if the backend is unreachable.
  const [rates, setRates] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage?.getItem(RATES_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.rates || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > RATES_TTL_MS) return null;
      return parsed.rates;
    } catch (_) {
      return null;
    }
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/currency/rates`);
        if (!res.ok) return;
        const data = await res.json();
        if (!alive || !data || !data.rates) return;
        setRates(data.rates);
        try {
          window.localStorage?.setItem(
            RATES_STORAGE_KEY,
            JSON.stringify({ rates: data.rates, savedAt: Date.now() }),
          );
        } catch (_) { /* ignore */ }
      } catch (_) {
        /* keep fallback rates */
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, currency);
    } catch (_) { /* ignore */ }
  }, [currency]);

  const setCurrency = useCallback((code) => {
    if (SUPPORTED_CURRENCIES.includes(code)) {
      setCurrencyState(code);
      track('currency_change', { currency: code });
    }
  }, []);

  /**
   * Convert a EUR-denominated amount to the active currency and format it
   * using the browser locale. All currencies are displayed as **whole
   * integers** (no decimals). Non-EUR amounts are **rounded UP (ceiling)**
   * so the displayed price is never lower than the true converted value —
   * safer for travel sites. EUR (base) is simply rounded to the nearest
   * integer (seed prices are whole EUR so this is a no-op in practice).
   *
   *   formatPrice(45)  →  "€45"  |  "$49"  |  "¥7,335"  |  "£39"
   */
  const formatPrice = useCallback((amountInEur) => {
    if (amountInEur == null || isNaN(amountInEur)) return '';
    const meta = CURRENCIES[currency] || CURRENCIES[DEFAULT_CURRENCY];
    // Prefer the live rate from the backend when available; fall back to
    // the hardcoded rate baked into CURRENCIES.
    const liveRate = rates && typeof rates[meta.code] === 'number' ? rates[meta.code] : null;
    const rate = meta.code === 'EUR' ? 1 : (liveRate ?? meta.rateFromEUR);
    const converted = Number(amountInEur) * rate;
    const rounded = meta.code === 'EUR'
      ? Math.round(converted)
      : Math.ceil(converted);
    try {
      const num = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(rounded);
      return `${num}${meta.symbol}`;
    } catch (_) {
      // Safari quirk fallback
      return `${rounded}${meta.symbol}`;
    }
  }, [currency, rates]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, CURRENCIES, SUPPORTED_CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
